import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components/macro';
import BasicInfoItem from './BasicInfoItem';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, setIn, getIn } from 'formik';
import { editCustomerField as editCustomerFieldAction } from '../actions';
import { validate } from '../../../helpers/validation';
import { Customer } from '../../types';
import { getBasicInfoUpdatesSelector } from '../selectors';
import { getBasicInfoUpdateForField } from '../customerHelpers';
import { LoadingOverlay } from '../../../components/Loading';
import { useTranslation } from 'react-i18next';
import Hover from '../../../components/Hover';
import { Checkbox } from '../../../components/Forms';
import { Button } from '../../../components/Buttons';

interface Option {
    [key: string]: string;
}

interface InputProps {
    formElement: 'input';
    value: string | undefined | null;
    type: string;
}

interface SelectProps {
    formElement: 'dropdown';
    value: string | undefined | null;
    options: Option[];
}

interface CheckboxProps {
    formElement: 'checkbox';
    value: boolean;
    label: string;
}

type CustomerInputProps = (InputProps | SelectProps | CheckboxProps) & {
    customer: Customer;
    accountId: string;
    validationFunction?: (message: string) => (value: string) => string | undefined;
    validationText?: string;
    notSetValue?: string;
    label: string;
    name: string;
    displayValue?: string;
};

type InputTypeProps = (InputProps | SelectProps | CheckboxProps) & {
    name: string;
    handleChange: (e: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleBlur: (e: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => void;
};

const InputType = (
    {
        name,
        handleChange,
        handleBlur,
        ...props
    }: InputTypeProps
) => {
    switch (props.formElement) {
        case 'input':
            return (
                <Input
                    type={props.type}
                    name={name}
                    value={props.value ?? undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            );
        case 'dropdown':
            if (!props.options) return null;
            return (
                <Input
                    as="select"
                    name={name}
                    value={props.value ?? undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                >
                    <option value="">---</option>
                    {props.options.map((option, i) => {
                        const [[val, label]] = Object.entries(option);
                        return (
                            <option key={i} value={val}>
                                {label}
                            </option>
                        );
                    })}
                </Input>
            );
        case 'checkbox':
            return (
                <Checkbox
                    name={name}
                    checked={props.value}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    label={<Label>{props.label}</Label>}
                />
            );
        default:
            return null;
    }
};

const CustomerInput = ({
    accountId,
    validationFunction,
    validationText,
    label,
    name,
    value,
    displayValue,
    notSetValue,
    customer,
    ...inputProps
}: CustomerInputProps) => {
    const dispatch = useDispatch();
    const basicInfoUpdates = useSelector(getBasicInfoUpdatesSelector);
    const { t } = useTranslation();

    const editCustomerField = useCallback(
        (value: { [name: string]: CustomerInputProps['value'] }) => {
            dispatch(editCustomerFieldAction(accountId, customer.customer_id, name, value));
        },
        [dispatch, name, accountId, customer.customer_id]
    );

    const [isEditing, setIsEditing] = useState(false);
    const toggleEditing = useCallback(() => setIsEditing((editing) => !editing), []);

    const updateStatus = getBasicInfoUpdateForField(basicInfoUpdates, name);
    const isLoading = updateStatus?.isLoading || false;
    const error = updateStatus?.error || null;

    useEffect(() => {
        if (!isLoading && !error) setIsEditing(false);
    }, [isLoading, error]);

    const initialValues = useMemo(() => setIn({}, name, value), [name, value]);

    const immediate = inputProps.formElement === 'checkbox';

    return (
        <Hover component="div">
            {(hovering) =>
                isEditing || immediate ? (
                    <Container>
                        <Formik
                            initialValues={initialValues}
                            validate={(values) => {
                                const errors: { [key: string]: string } = {};
                                if (!validationFunction || !validationText) return errors;
                                return validate(name, validationFunction(validationText), values, errors);
                            }}
                            onSubmit={editCustomerField}
                            onReset={toggleEditing}
                        >
                            {({
                                values,
                                errors,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                touched,
                                setFieldValue,
                                submitForm,
                                resetForm,
                            }) => {
                                const changeHandler =
                                    inputProps.formElement === 'checkbox'
                                        ? (e: React.ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue(name, e.currentTarget.checked);
                                            submitForm();
                                        }
                                        : handleChange;
                                const inputTypeProps = {
                                    ...inputProps,
                                    name,
                                    value: getIn(values, name) ?? '',
                                    handleChange: changeHandler,
                                    handleBlur,
                                    label,
                                };
                                return (
                                    <form onSubmit={handleSubmit}>
                                        {inputProps.formElement !== 'checkbox' && <Label>{label}</Label>}
                                        <InputContainer>
                                            <InputType {...inputTypeProps as any} />
                                            {!immediate && (
                                                <>
                                                    <Button
                                                        className={'tiny alt'}
                                                        onClick={() => resetForm()}
                                                        disabled={isLoading}
                                                        color="secondary"
                                                    >
                                                        {t('customers.fields.form.cancel')}
                                                    </Button>
                                                    <Button
                                                        className={'tiny'}
                                                        type="submit"
                                                        disabled={isLoading}
                                                        color="primary"
                                                    >
                                                        {t('customers.fields.form.save')}
                                                    </Button>
                                                </>
                                            )}
                                        </InputContainer>
                                        {getIn(touched, name) && getIn(errors, name) && (
                                            <ErrorMessage>{getIn(errors, name)}</ErrorMessage>
                                        )}
                                    </form>
                                );
                            }}
                        </Formik>
                        {isLoading && <LoadingOverlay />}
                    </Container>
                ) : (
                    <BasicInfoItem
                        value={value}
                        displayValue={displayValue}
                        notSetValue={notSetValue}
                        isHovering={hovering}
                        label={label}
                        onClick={toggleEditing}
                    />
                )
            }
        </Hover>
    );
};

export default CustomerInput;

const Container = styled.div`
    margin: 14px 0;
`;

const InputContainer = styled.div`
    display: flex;
    align-items: baseline;
`;

const Label = styled.label`
    font-size: 14px;
    color: #495e61;
    font-weight: 700;
`;

const ErrorMessage = styled.span`
    font-size: 14px;
    color: #c71b1b;
    text-align: center;
    display: block;
    margin-top: 3px;
`;

const Input = styled.input`
    background: #fdffff;
    border-radius: 2px;
    border: none;
    border-bottom: 2px solid rgba(92, 103, 113, 0.2);
    font-size: 14px;
    display: block;
    margin-top: 4px;
    padding: 0 15px;
    flex: 1 0 auto;
    height: 34px;
    outline: none;
    & ~ Button {
        margin-left: 5px;
    }
`;
